import { forwardRef, useId, type ComponentPropsWithoutRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { labelClass } from '@/shared/ui/core/styles';

export type CheckboxSize = 'sm' | 'md' | 'lg';

export type CheckboxProps = Omit<ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, 'checked'> & {
  checked?: boolean | 'indeterminate';
  indeterminate?: boolean;
  label?: string;
  description?: string;
  size?: CheckboxSize;
};

const sizeClasses: Record<CheckboxSize, { box: string; icon: string; square: string; text: string }> = {
  sm: { box: 'size-4 rounded-[3px]', icon: 'size-3', square: 'size-2 rounded-[1px]', text: 'text-[12px]' },
  md: { box: 'size-[18px] rounded-[4px]', icon: 'size-3.5 stroke-[2.5]', square: 'size-2.5 rounded-[1.5px]', text: 'text-caption' },
  lg: { box: 'size-[22px] rounded-[5px]', icon: 'size-4 stroke-[2.5]', square: 'size-3 rounded-[2px]', text: 'text-body' },
};

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { className, label, description, size = 'md', id, checked, indeterminate, ...props },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const s = sizeClasses[size];

  const isIndeterminate = indeterminate || checked === 'indeterminate';
  const effectiveChecked = isIndeterminate ? 'indeterminate' : checked;

  return (
    <div className={cn('flex gap-2.5 select-none', description ? 'items-start' : 'items-center')}>
      <CheckboxPrimitive.Root
        ref={ref}
        id={fieldId}
        checked={effectiveChecked}
        className={cn(
          'group flex shrink-0 items-center justify-center border border-border-strong bg-surface cursor-pointer transition-colors',
          s.box,
          description && 'mt-0.5',
          'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-on-primary',
          'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-surface',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center">
          {isIndeterminate ? (
            <span className={cn('block bg-primary', s.square)} aria-hidden />
          ) : (
            <>
              <span className={cn('hidden group-data-[state=indeterminate]:block bg-primary', s.square)} aria-hidden />
              <Check className={cn('group-data-[state=indeterminate]:hidden', s.icon)} aria-hidden />
            </>
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {(label || description) && (
        <div className="flex flex-col gap-0.5">
          {label ? (
            <label
              htmlFor={fieldId}
              className={cn(
                labelClass,
                s.text,
                'cursor-pointer select-none',
                !description && 'leading-none',
              )}
            >
              {label}
            </label>
          ) : null}
          {description ? <p className="text-caption text-text-secondary">{description}</p> : null}
        </div>
      )}
    </div>
  );
});
